/***********************/
/* Angular + external */
import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChange,
  forwardRef,
  AfterViewInit,
  Input,
  Output,
  ViewChild,
  EventEmitter,
  ElementRef,
} from "@angular/core";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";

/***********************/
/* BFX */
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { CcyPairLookupService, CcyPairPromiseLookupService } from "shared/cubiq-services";
// models
import { BfxInputControlStateEnum } from "../bfx-interface-controls/models/bfx-interface-controls.enums";
import { BfxAutocompleteGenericModel } from "../bfx-interface-controls/models/bfx-interface-controls.models";

export const PAIR_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => ICCcyPairDefaultsComponent),
  multi: true,
};
import { LoggerService } from "@bfx/logger";
import { BfxAutoCompleteComponent } from "./bfx-autocomplete.component";
const logger = LoggerService.for(__dirname);

@Component({
  selector: "ic-ccy-pair-defaults",
  templateUrl: "./html/ic-ccy-pair-defaults.html",
  providers: [PAIR_VALUE_ACCESSOR],
  standalone: false,
})
export class ICCcyPairDefaultsComponent
  implements OnInit, OnDestroy, OnChanges, ControlValueAccessor
{
  /***************************************/
  /* COMPONENT INTERFACE DATA */
  /* set this flag to true when the component is used in order entry defaults */
  @Input("orderEntryDefaults") input_modeDefaults: boolean = false;
  /* if true threats null or empty value as valid (its used in order entry defaults) */
  @Input("allowEmpty") input_allowEmpty: boolean = false;

  @Input("label") input_label: string;

  @Input("dataset") input_dataset: Array<any>;

  @Input("marketRateDisable") market_rate_disabled: boolean = false;

  @Input("disabled") input_disabled: boolean;

  @Output() blur: EventEmitter<any> = new EventEmitter<any>();

  @Output() focus: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild("inputField") inputField: BfxAutoCompleteComponent;

  onModelChange: Function = (value: any) => {};
  onModelTouched: Function = () => {};

  /* External Validation */
  input_externalState: BfxInputControlStateEnum;

  private get inputFieldValue(): string {
    return this.inputField?.inputEL?.nativeElement?.value;
  }

  private set inputFieldValue(value: string) {
    if (this.inputField?.inputEL?.nativeElement) {
      this.inputField.inputEL.nativeElement.value = value;
    }
  }

  @Input()
  get externalState() {
    return this.input_externalState;
  }

  set externalState(value: any) {
    this.input_externalState = value;
  }

  /* this flag is used for immediately checking */
  input_validationState: BfxInputControlStateEnum;
  @Output()
  validationStateChange: EventEmitter<BfxInputControlStateEnum | null> =
    new EventEmitter<BfxInputControlStateEnum | null>();
  @Input()
  get validationState() {
    return this.input_validationState;
  }

  set validationState(value: any) {
    this.input_validationState = value;
    this.validationStateChange.emit(this.input_validationState);
  }

  /***************************************/
  /* DATA */
  public config: any = {
    resultsToShow: 30,
    favoritesSize: 3, // default value, loaded on ngOnInit
    removeFavoriteDuplicates: true,
  };
  public data: any = {
    value: null,
    ccyPairModel: null,
    dataset: [],
    filteredItems: [],
    inverted: false,
    label: "Pair",
    disabled: false,
    state: BfxInputControlStateEnum.INACTIVE,
  };

  public locked: boolean = false;

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */

  constructor(
    public element: ElementRef,
    public translationService: TranslateService,
    protected currencyPairService: CcyPairLookupService,
  ) {}

  ngOnInit() {
    this.initialize();
  }

  ngOnDestroy() {}

  ngOnChanges() {
    this.initialize();
  }
  /**********************************************************************/
  /* INITIALIZATION */

  protected initialize(): void {
    this.data.label = this.input_label;

    if (this.input_dataset.length !== this.data.dataset.length) {
      this.data.dataset = this.transformArrayToGenericModel(this.input_dataset);
    }

    if (this.input_externalState != null) {
      this.data.state = this.input_externalState;
      this.validationState = this.input_externalState;
    }
  }

  /**********************************************************************/
  /* ControlValueAccessor */

  writeValue(value: any): void {
    this.data.value =
      value != null && typeof value === "string" && value.length > 0 ? value.toUpperCase() : value;

    this.updateInverted(value);
    this.inputFieldValue = value;
  }

  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  /**********************************************************************/
  /* PUBLIC METHODS */

  public resetInput() {
    this.data.disabled = false;
    this.data.label = "Pair";

    this.data.filteredItems = [];

    this.externalState = null;

    this.initialize();
    this.resetValue("", BfxInputControlStateEnum.INACTIVE);
  }

  public lockInputControl(): void {
    this.locked = true;
  }

  public unlockInputControl(): void {
    this.locked = false;
  }

  public autocompleteSearch($event: any): void {
    let filteredItems = [];
    let itemsAll = this.data.dataset;
    let searchToken = $event.query;

    // HANDLE REGULAR SUGGESTIONS
    let favoritesSize = filteredItems.length;
    let suggestions = new Array<BfxAutocompleteGenericModel>();
    for (let i = 0; i < itemsAll.length; i++) {
      let item = itemsAll[i];
      if (
        searchToken.length === 0 ||
        item["value"].toLowerCase().startsWith(searchToken.toLowerCase())
      ) {
        if (
          !this.config.removeFavoriteDuplicates ||
          (filteredItems.findIndex((favItem) => {
            return favItem.value === item.value;
          }) < 0 &&
            suggestions.findIndex((inItem) => {
              return inItem.value === item.value;
            }) < 0)
        ) {
          // avoid duplicates
          suggestions.push(item);
        }
      }

      if (
        this.config.resultsToShow > 0 &&
        suggestions.length >= this.config.resultsToShow - favoritesSize
      ) {
        suggestions.sort((a, b) => a.value.localeCompare(b.value));
        this.data.filteredItems = filteredItems.concat(suggestions);
        return;
      }
    }
    suggestions.sort((a, b) => a.value.localeCompare(b.value));
    this.data.filteredItems = filteredItems.concat(suggestions);
  }

  public onFocus($event): void {
    this.data.state = BfxInputControlStateEnum.ACTIVE;
    this.validationState = BfxInputControlStateEnum.ACTIVE;
    this.focus.emit($event);
  }

  public onBlur($event): void {
    this.onModelTouched();

    if (this.validatePair() || this.data.ccyPairModel === null) {
      let value: null;
      if (
        this.data.ccyPairModel != null &&
        this.data.ccyPairModel instanceof BfxAutocompleteGenericModel &&
        this.data.ccyPairModel.value != null &&
        this.data.ccyPairModel.value.length === 6
      ) {
        value = this.data.ccyPairModel.value;
      }
      //let value = this.data.ccyPairModel != null && this.data.ccyPairModel.value != null && this.data.ccyPairModel.value.length === 6 ? this.data.ccyPairModel.value : null;
      this.writeValue(value);
      this.onChange(value);
    }
    this.blur.emit($event);
    this.validationStateChange.emit(this.validationState);
  }
  public onSelect($event): void {
    this.onBlur($event);
  }

  public onChange($event: any): void {
    this.onModelChange($event); // $event
  }

  /**********************************************************************/
  /* PRIVATE METHODS */
  public validatePair(): boolean {
    if (typeof this.inputFieldValue === "string" && this.inputFieldValue.length > 0) {
      let selectedItem = this.inputFieldValue;
      let selectedModel = null;
      if (selectedItem != null && typeof selectedItem === "string" && selectedItem.length === 6) {
        selectedModel = this.data.dataset.find(
          (item) => item["value"].toLowerCase() === selectedItem.toLowerCase(),
        );
      }

      if (selectedModel != null) {
        this.updateInverted(selectedItem);
        if (typeof this.data.ccyPairModel === "string") {
          // assign model if string
          this.data.ccyPairModel = selectedModel;
        }
        this.data.state = BfxInputControlStateEnum.VALID;
        this.validationState = BfxInputControlStateEnum.VALID;
        this.externalState = null;
        return true;
      } else {
        this.resetValue(selectedItem, BfxInputControlStateEnum.INVALID);
      }
    } else if (this.input_allowEmpty) {
      this.resetValue("", BfxInputControlStateEnum.INACTIVE);
    } else {
      this.resetValue("", BfxInputControlStateEnum.INVALID);
    }
    return false;
  }

  private updateInverted(value: string) {
    if (typeof value === "string" && value.length === 6) {
      let base = value.substring(0, 3);
      let nonBase = value.substring(3, 6);
      let pair = this.currencyPairService.getCcyPair(base, nonBase);
      this.data.inverted = pair?.base == nonBase;
    } else {
      this.data.inverted = false;
    }
  }

  private resetValue(selectedItem: string, state: BfxInputControlStateEnum) {
    this.data.value = null;
    this.data.inverted = false;
    this.data.state = state;

    this.data.ccyPairModel = selectedItem;
    this.validationState = state;
    this.onChange(null);

    if (
      state === BfxInputControlStateEnum.INVALID &&
      typeof selectedItem === "string" &&
      selectedItem.length === 6
    ) {
      setTimeout(() => {
        this.inputFieldValue = selectedItem;
      }, 0);
    }
  }

  private transformArrayToGenericModel(data: Array<string>): Array<BfxAutocompleteGenericModel> {
    let result: Array<BfxAutocompleteGenericModel> = Array<BfxAutocompleteGenericModel>();
    if (data != null && data.length > 0) {
      data.forEach((item) => {
        try {
          result.push(this.transformItemToGenericModel(item));
        } catch (ex) {
          logger.out.warn(ex);
        }
      });
    }
    return result;
  }

  protected transformItemToGenericModel(item: string): BfxAutocompleteGenericModel {
    if (item != null) {
      return new BfxAutocompleteGenericModel(item, item);
    } else {
      throw "Can not transform as CcyPair is null!";
    }
  }

  public setCcyPairExternally(ccyPair: string): void {
    this.data.ccyPairModel = this.transformItemToGenericModel(ccyPair);
    this.writeValue(ccyPair);
    this.onModelChange(ccyPair);
    this.validatePair();
  }
}
